<script setup>
import { computed } from 'vue';
import VueSlider from 'vue-3-slider-component';
import { cloneObject, getRange } from 'utils';
import { useStore } from 'vuex';
import { useAnalytics } from 'src/composables/analytics';

const props = defineProps({
  options: {
    type: Object,
    required: false,
    default: () => {}
  },
  label: {
    type: String,
    required: true
  },
  filterKey: {
    type: String,
    required: true
  },
  filterCode: {
    type: String,
    required: true
  },
  value: {
    type: [Number, Array, String],
    required: false,
    default: 0
  }
});

const store = useStore();

const { trackEvent } = useAnalytics();

const aggregations = computed(() => store.getters.aggregations);
const urlFilters = computed(() => store.getters.urlFilters);

const valueAggs = computed(() => {
  if (aggregations.value) {
    let aggs = null;
    if (aggregations.value.hasOwnProperty(props.filterKey)) aggs = aggregations.value[props.filterKey];
    else if (aggregations.value.hasOwnProperty(props.filterCode)) aggs = aggregations.value[props.filterCode];

    if (aggs && typeof aggs === 'object' && aggs.hasOwnProperty('min') && aggs.hasOwnProperty('max')) {
      return {
        min: aggs.min.value,
        max: aggs.max.value
      };
    }
  }

  return null;
});

const min = computed(() => {
  if (valueAggs.value) return valueAggs.value.min;

  return props.options.min ? props.options.min : 0;
});

const max = computed(() => {
  if (valueAggs.value) return valueAggs.value.max;

  return props.options.max ? props.options.max : 100;
});

const sliderData = computed(() => [0, ...getRange(min.value, max.value)]);

const anyLabel = computed(() => (props.options && props.options.anyLabel ? props.options.anyLabel : 'any'));

function tooltipFormatter(value) {
  if (props.options && props.options.unit) {
    return (value !== 0 || props.options.range) ? `${value}${props.options.unit}` : anyLabel.value;
  }

  return value;
}

const internalValue = computed(() => {
  if (props.options.range) {
    return Array.isArray(props.value) ? [parseInt(props.value[0]), parseInt(props.value[1])] : [min.value, max.value];
  }

  return props.value !== null ? parseInt(props.value) : 0;
});

function handleUpdate(val) {
  const url = cloneObject(urlFilters.value);
  url[props.filterKey] = val;

  store.dispatch('setUrlFilters', url);

  trackEvent('filter', 'toggle', {
    filterType: 'range',
    filterLabel: props.label,
    filterKey: props.filterKey,
    value: val
  });
}
</script>

<template>
  <div class="ce-rangeFilter">
    <div
      class="ce-rangeFilter-headline"
      v-html="label" />

    <vue-slider
      :model-value="internalValue"
      @update:modelValue="handleUpdate"
      :lazy="true"
      :min="min"
      :max="max"
      tooltip="always"
      tooltip-placement="bottom"
      :tooltip-formatter="tooltipFormatter"
      :clickable="true"
      :data="sliderData" />
  </div>
</template>

<style lang="less">
  @import (reference) "~styles/includes/vars";
  @sliderMainColor: @ce-slider-filter-main-color;

  .ce-rangeFilter {
    padding: @space-3 0 @space-5;

    .vue-slider {
      padding: @space-2;
    }

    .ce-rangeFilter-headline {
      margin-bottom: @space-2;
      font-weight: 600;
    }

    .vue-slider-disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .vue-slider-rail {
      background-color: @color-mono-92;
      border-radius: 15px;
    }

    .vue-slider-process {
      background-color: @sliderMainColor;
      border-radius: 15px;
    }

    .vue-slider-mark {
      z-index: 4;

      &:first-child .vue-slider-mark-step, &:last-child .vue-slider-mark-step {
        display: none;
      }
    }

    .vue-slider-mark-step {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background-color: rgba(0, 0, 0, 0.16);
    }
    .vue-slider-mark-label {
      font-size: 14px;
      white-space: nowrap;
    }
    .vue-slider-dot-handle {
      cursor: pointer;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background-color: @sliderMainColor;
      box-sizing: border-box;
      box-shadow: 0.5px 0.5px 2px 1px rgba(0, 0, 0, 0.32);
    }
    .vue-slider-dot-handle-focus {
      box-shadow: 0 0 1px 2px rgba(52, 152, 219, 0.36);
    }

    .vue-slider-dot-handle-disabled {
      cursor: not-allowed;
      background-color: @color-mono-40;
    }

    .vue-slider-dot-tooltip-inner {
      font-size: 14px;
      white-space: nowrap;
      padding: 2px 5px;
      min-width: 20px;
      text-align: center;
      color: #fff;
      border-radius: 5px;
      border-color: @sliderMainColor;
      background-color: @sliderMainColor;
      box-sizing: content-box;
      font-weight: 600;

      &::after {
        content: "";
        position: absolute;
      }
    }

    .vue-slider-dot-tooltip-inner-top::after {
      top: 100%;
      left: 50%;
      transform: translate(-50%, 0);
      height: 0;
      width: 0;
      border-color: transparent;
      border-style: solid;
      border-width: 5px;
      border-top-color: inherit;
    }
    .vue-slider-dot-tooltip-inner-bottom::after {
      bottom: 100%;
      left: 50%;
      transform: translate(-50%, 0);
      height: 0;
      width: 0;
      border-color: transparent;
      border-style: solid;
      border-width: 5px;
      border-bottom-color: inherit;
    }
    .vue-slider-dot-tooltip-inner-left::after {
      left: 100%;
      top: 50%;
      transform: translate(0, -50%);
      height: 0;
      width: 0;
      border-color: transparent;
      border-style: solid;
      border-width: 5px;
      border-left-color: inherit;
    }
    .vue-slider-dot-tooltip-inner-right::after {
      right: 100%;
      top: 50%;
      transform: translate(0, -50%);
      height: 0;
      width: 0;
      border-color: transparent;
      border-style: solid;
      border-width: 5px;
      border-right-color: inherit;
    }

    .vue-slider-dot-tooltip-wrapper {
      opacity: 0;
      transition: all 0.3s;
    }
    .vue-slider-dot-tooltip-wrapper-show {
      opacity: 1;
    }
  }
</style>
